import { useCallback, useEffect, useRef, useState } from 'react';

import { type Bounds } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import {
  applyWheel,
  beginPan,
  type Camera,
  continuePan,
  type PanState,
  Renderer,
} from '@/rendering';

/**
 * The single bridge between React's lifecycle and the imperative render loop.
 *
 * React mounts one empty `<div>`; the `Renderer` creates the canvases inside it and owns
 * everything from that point on. **React never re-renders the canvas.** Pointer moves,
 * pan, zoom and tool gestures all run through imperative callbacks, because a re-render
 * per pointer-move would spend the whole 16.7 ms frame budget on reconciliation before a
 * single pixel is drawn.
 *
 * ## Which gesture wins
 *
 * Navigation and editing share the same pointer, so the split has to be unambiguous:
 *
 * | Gesture                     | Goes to    |
 * | --------------------------- | ---------- |
 * | Middle button, or space-drag | Pan        |
 * | Wheel / trackpad            | Pan        |
 * | Ctrl+wheel, pinch           | Zoom       |
 * | Left button                 | Active tool|
 * | Right button                | Menu       |
 *
 * Space-to-pan rather than a modifier because every modifier is already spoken for by the
 * tools (shift constrains, ctrl suppresses snapping).
 */

export interface PointerGesture {
  readonly world: Vec2;
  readonly screen: Vec2;
  readonly button: number;
  readonly modifiers: { shift: boolean; alt: boolean; primary: boolean };
}

export interface CanvasHostProps {
  readonly onCameraChange?: (camera: Camera) => void;
  readonly onReady?: (renderer: Renderer) => void;
  readonly initialFit?: Bounds;
  readonly onPointerDown?: (gesture: PointerGesture) => void;
  readonly onPointerMove?: (gesture: PointerGesture) => void;
  readonly onPointerUp?: (gesture: PointerGesture) => void;
  readonly onPointerLeave?: () => void;
  /** Double-click, which the shell uses to open a label's editor. */
  readonly onDoubleClick?: (gesture: PointerGesture) => void;
  /** Right-click. The shell decides what the menu says; the canvas only reports where. */
  readonly onContextMenu?: (gesture: PointerGesture) => void;
  readonly cursor?: string;
}

export function CanvasHost({
  onCameraChange,
  onReady,
  initialFit,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onDoubleClick,
  onContextMenu,
  cursor = 'default',
}: CanvasHostProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const [ready, setReady] = useState(false);

  // Handlers are read through refs so the event-listener effect never has to re-run when
  // a parent re-renders with a new closure — re-binding listeners mid-drag drops the
  // gesture. Written in an effect rather than during render: React may render a component
  // without committing it, and a ref written on an abandoned render would be stale.
  const handlers = useRef<{
    onPointerDown?: ((gesture: PointerGesture) => void) | undefined;
    onPointerMove?: ((gesture: PointerGesture) => void) | undefined;
    onPointerUp?: ((gesture: PointerGesture) => void) | undefined;
    onPointerLeave?: (() => void) | undefined;
    onDoubleClick?: ((gesture: PointerGesture) => void) | undefined;
    onContextMenu?: ((gesture: PointerGesture) => void) | undefined;
  }>({});
  const cameraChange = useRef<((camera: Camera) => void) | undefined>(undefined);

  useEffect(() => {
    handlers.current = {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerLeave,
      onDoubleClick,
      onContextMenu,
    };
    cameraChange.current = onCameraChange;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    const renderer = new Renderer(container);
    rendererRef.current = renderer;

    const rect = container.getBoundingClientRect();
    renderer.resize(rect.width, rect.height);
    onReady?.(renderer);
    if (initialFit !== undefined) renderer.fit(initialFit);
    setReady(true);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      renderer.resize(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      renderer.dispose();
      rendererRef.current = null;
      setReady(false);
    };
    // Mount-only: the renderer owns the canvas for this component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const renderer = rendererRef.current;
    if (container === null || renderer === null || !ready) return;

    let pan: PanState | null = null;
    let panPointerId: number | null = null;
    let spaceHeld = false;

    const localPoint = (event: MouseEvent): Vec2 => {
      const rect = container.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const gestureOf = (event: PointerEvent): PointerGesture => {
      const screen = localPoint(event);
      return {
        screen,
        world: renderer.screenToWorld(screen),
        button: event.button,
        modifiers: {
          shift: event.shiftKey,
          alt: event.altKey,
          primary: event.ctrlKey || event.metaKey,
        },
      };
    };

    const emitCamera = (next: Camera): void => {
      renderer.setCamera(next);
      cameraChange.current?.(next);
    };

    const wantsPan = (event: PointerEvent): boolean => event.button === 1 || spaceHeld;

    const handlePointerDown = (event: PointerEvent): void => {
      container.setPointerCapture(event.pointerId);

      if (wantsPan(event)) {
        pan = beginPan(localPoint(event));
        panPointerId = event.pointerId;
        event.preventDefault();
        return;
      }
      if (event.button !== 0) return;
      event.preventDefault();
      handlers.current.onPointerDown?.(gestureOf(event));
    };

    const handlePointerMove = (event: PointerEvent): void => {
      if (pan !== null && event.pointerId === panPointerId) {
        const result = continuePan(pan, renderer.camera, localPoint(event));
        pan = result.state;
        emitCamera(result.camera);
        return;
      }
      handlers.current.onPointerMove?.(gestureOf(event));
    };

    const handlePointerUp = (event: PointerEvent): void => {
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      if (pan !== null && event.pointerId === panPointerId) {
        pan = null;
        panPointerId = null;
        return;
      }
      handlers.current.onPointerUp?.(gestureOf(event));
    };

    const handlePointerLeave = (): void => {
      handlers.current.onPointerLeave?.();
    };

    const handleDoubleClick = (event: MouseEvent): void => {
      // Selecting the canvas text on a double-click would leave a blue smear over the
      // drawing, and there is no text there to select.
      event.preventDefault();
      const point = localPoint(event);
      handlers.current.onDoubleClick?.({
        screen: point,
        world: renderer.screenToWorld(point),
        button: event.button,
        modifiers: {
          shift: event.shiftKey,
          alt: event.altKey,
          primary: event.ctrlKey || event.metaKey,
        },
      });
    };

    const handleContextMenu = (event: MouseEvent): void => {
      // The browser's own menu is about the page — reload, view source, save image — and none
      // of it is about the drawing. Replacing it is the point.
      event.preventDefault();
      const point = localPoint(event);
      handlers.current.onContextMenu?.({
        screen: point,
        world: renderer.screenToWorld(point),
        button: event.button,
        modifiers: {
          shift: event.shiftKey,
          alt: event.altKey,
          primary: event.ctrlKey || event.metaKey,
        },
      });
    };

    const handleWheel = (event: WheelEvent): void => {
      // Non-passive and prevented, or the browser page-zooms on ctrl+wheel and scrolls the
      // surrounding layout on a plain one.
      event.preventDefault();
      emitCamera(
        applyWheel(renderer.camera, renderer.viewport, localPoint(event), {
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          // A trackpad pinch arrives as a wheel event with `ctrlKey` set, which is why the
          // deliberate chord and the gesture are one flag here.
          zoom: event.ctrlKey || event.metaKey,
          shift: event.shiftKey,
        })
      );
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.code === 'Space' && !spaceHeld) {
        spaceHeld = true;
        container.style.cursor = 'grab';
      }
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.code === 'Space') {
        spaceHeld = false;
        container.style.cursor = '';
      }
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerUp);
    container.addEventListener('pointerleave', handlePointerLeave);
    container.addEventListener('dblclick', handleDoubleClick);
    container.addEventListener('contextmenu', handleContextMenu);
    container.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerUp);
      container.removeEventListener('dblclick', handleDoubleClick);
      container.removeEventListener('contextmenu', handleContextMenu);
      container.removeEventListener('pointerleave', handlePointerLeave);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [ready]);

  const setContainer = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
  }, []);

  return (
    <div
      ref={setContainer}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        touchAction: 'none',
        cursor,
        background: 'var(--es-canvas)',
      }}
    />
  );
}
